import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { FiEdit, FiTrash2, FiUploadCloud } from 'react-icons/fi';

import FormField from '../FormField';
import { IField } from '../interfaces/IField';
import VariantModal from './VariantModal';
import { productSchema } from './schema/product.schema';

import Button from '@/components/ui/button';
import { MAX_IMAGE_FILE_SIZE } from '@/components/utils/constants';
import { formatPrice } from '@/components/utils/utils';
import {
	ICreateProductPayload,
	ICreateProductVariantPayload,
} from '@/interfaces/api/product/ICreateProductPayload';
import { haggleApiService } from '@/services/haggle.service';

const initialValues: ICreateProductPayload = {
	handle: '',
	title: '',
	bodyHtml: '',
	productInfo: '',
	// vendor: '',
	type: '',
	tags: '',
	published: true,
	imageSrc: '',
	variants: [],
};

const formFields: IField[] = [
	{
		name: 'title',
		label: 'Product Title *',
		type: 'text',
		placeholder: 'Enter product title',
	},
	{
		name: 'type',
		label: 'Product Type',
		type: 'text',
		placeholder: 'Enter product type',
	},
	{
		name: 'tags',
		label: 'Tags (comma-separated)',
		type: 'text',
		placeholder: 'Enter tags (comma-separated, e.g. "mobile, phone, apple")',
	},
	{
		name: 'bodyHtml',
		label: 'Description *',
		type: 'textarea',
		rows: 2,
		placeholder: 'Enter product description',
	},
	{
		name: 'productInfo',
		label: 'Additional Information',
		type: 'textarea',
		rows: 2,
		placeholder: 'Enter additional product information',
	},
];

type PropTypes = {
	handleSubmit: (values: ICreateProductPayload) => Promise<void>;
	loading: boolean;
	setIsLoading: (isLoading: boolean) => void;
	initialValues?: ICreateProductPayload;
	closeModal: () => void;
	isEditing?: boolean;
};

interface CustomErrors {
	image: string;
	variants: string;
}

const ProductForm = ({
	handleSubmit,
	loading,
	setIsLoading,
	initialValues: propInitialValues = initialValues,
	isEditing = false,
}: PropTypes) => {
	const [imageFile, setImageFile] = useState<File | null>(null);
	const [initialImage, setInitialImage] = useState<string | null>(
		propInitialValues.imageSrc || null,
	);

	// Variant modal and state - initialize with existing variants if editing
	const [variants, setVariants] = useState<ICreateProductVariantPayload[]>(
		propInitialValues.variants || [],
	);
	const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
	const [editingVariantIndex, setEditingVariantIndex] = useState<number | null>(
		null,
	);
	const [isEditingVariant, setIsEditingVariant] = useState(false);

	// Shared uploaded images state
	const [uploadedImages, setUploadedImages] = useState<string[]>([]);

	// Track all variant images (both files and URLs)
	const [variantImageFiles, setVariantImageFiles] = useState<File[]>([]);

	// Custom error state
	const [customErrors, setCustomErrors] = useState<CustomErrors>({
		image: '',
		variants: '',
	});

	// Update initial image when props change
	useEffect(() => {
		setInitialImage(propInitialValues.imageSrc || null);

		// Initialize uploaded images with existing images
		const existingImages: string[] = [];
		if (propInitialValues.imageSrc) {
			existingImages.push(propInitialValues.imageSrc);
		}
		if (propInitialValues.variants) {
			propInitialValues.variants.forEach((variant) => {
				if (variant.image && !existingImages.includes(variant.image)) {
					existingImages.push(variant.image);
				}
			});
		}
		setUploadedImages(existingImages);

		// Initialize variant image files (empty for existing products since they're already uploaded)
		setVariantImageFiles([]);

		// Update variants state with initial values
		setVariants(propInitialValues.variants || []);
	}, [propInitialValues.imageSrc, propInitialValues.variants]);

	// Validate image
	const validateImage = (): boolean => {
		try {
			const hasImage = imageFile || initialImage;
			if (!hasImage) {
				setCustomErrors((prev) => ({
					...prev,
					image: 'Product image is required',
				}));
				return false;
			}
			setCustomErrors((prev) => ({ ...prev, image: '' }));
			return true;
		} catch (error) {
			console.error('Error validating image:', error);
			setCustomErrors((prev) => ({
				...prev,
				image: 'Product image is required',
			}));
			return false;
		}
	};

	// Validate variants
	const validateVariants = (): boolean => {
		try {
			if (variants.length === 0) {
				setCustomErrors((prev) => ({
					...prev,
					variants: 'At least one variant is required',
				}));
				return false;
			}
			setCustomErrors((prev) => ({ ...prev, variants: '' }));
			return true;
		} catch (error) {
			console.error('Error validating variants:', error);
			setCustomErrors((prev) => ({
				...prev,
				variants: 'At least one variant is required',
			}));
			return false;
		}
	};

	// Validate all custom fields
	const validateCustomFields = (): boolean => {
		const imageValid = validateImage();
		const variantsValid = validateVariants();
		return imageValid && variantsValid;
	};

	// Formik validate function
	const formikValidate = () => {
		const isValid = validateCustomFields();
		return isValid
			? {}
			: { customFields: 'Product variant or image is not valid' };
	};

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files) return;
		const file = e.target.files[0];
		if (!file) return;
		if (file.size > MAX_IMAGE_FILE_SIZE) {
			setCustomErrors((prev) => ({
				...prev,
				image: 'File size must be less than 5MB',
			}));
			e.target.value = '';
			return;
		}

		setImageFile(file);

		// Clear image error when file is selected
		setCustomErrors((prev) => ({ ...prev, image: '' }));
	};

	// Add function to handle image upload and add to shared state
	const handleImageUpload = async (file: File): Promise<string> => {
		const imageUrl = await haggleApiService.uploadImage(file);
		setUploadedImages((prev) => [...prev, imageUrl]);
		return imageUrl;
	};

	const handleImageDelete = () => {
		// If there's an uploaded file, remove it first
		if (imageFile) {
			setImageFile(null);
		} else if (initialImage) {
			// If no uploaded file but there's an initial image, remove the initial image
			setInitialImage(null);
		}

		// Set error when image is deleted
		setCustomErrors((prev) => ({
			...prev,
			image: 'Product image is required',
		}));
	};

	const handleAddVariant = (variant: ICreateProductVariantPayload) => {
		setVariants((prev) => [...prev, variant]);

		// Track new variant image files for sharing between variants
		if (
			variant.imageFile &&
			(variant.image === 'new-upload' ||
				variant.image === 'current-product-image')
		) {
			setVariantImageFiles((prev) => [...prev, variant.imageFile!]);
		}

		// Clear variants error when variant is added
		setCustomErrors((prev) => ({ ...prev, variants: '' }));
	};

	const handleUpdateVariant = (
		updatedVariant: ICreateProductVariantPayload,
		index: number,
	) => {
		setVariants((prev) => {
			const newVariants = [...prev];
			const oldVariant = newVariants[index];

			// Remove old variant image file if it was different
			if (
				oldVariant.imageFile &&
				oldVariant.imageFile !== updatedVariant.imageFile
			) {
				setVariantImageFiles((prevFiles) =>
					prevFiles.filter((file) => file !== oldVariant.imageFile),
				);
			}

			// Add new variant image file if it exists
			if (
				updatedVariant.imageFile &&
				(updatedVariant.image === 'new-upload' ||
					updatedVariant.image === 'current-product-image')
			) {
				setVariantImageFiles((prevFiles) => [
					...prevFiles,
					updatedVariant.imageFile!,
				]);
			}

			// Preserve the original ID if it exists (safety fallback)
			if (oldVariant.id && !updatedVariant.id) {
				updatedVariant.id = oldVariant.id;
			}

			newVariants[index] = updatedVariant;
			return newVariants;
		});

		// Clear variants error when variant is updated
		setCustomErrors((prev) => ({ ...prev, variants: '' }));
	};

	const handleRemoveVariant = (index: number) => {
		const variantToRemove = variants[index];
		const newVariants = variants.filter((_, i) => i !== index);
		setVariants(newVariants);

		// Remove the associated image file from variantImageFiles if it exists
		if (variantToRemove.imageFile) {
			setVariantImageFiles((prev) =>
				prev.filter((file) => file !== variantToRemove.imageFile),
			);
		}

		// Set error if no variants left
		if (newVariants.length === 0) {
			setCustomErrors((prev) => ({
				...prev,
				variants: 'At least one variant is required',
			}));
		}
	};

	const handleEditVariant = (index: number) => {
		setEditingVariantIndex(index);
		setIsEditingVariant(true);
		setIsVariantModalOpen(true);
	};

	const handleOpenVariantModal = () => {
		setIsEditingVariant(false);
		setEditingVariantIndex(null);
		setIsVariantModalOpen(true);
	};

	const handleCloseVariantModal = () => {
		setIsVariantModalOpen(false);
		setIsEditingVariant(false);
		setEditingVariantIndex(null);
	};

	const handleFormSubmit = async (values: ICreateProductPayload) => {
		setIsLoading(true);
		// Validate custom fields first
		if (!validateCustomFields()) {
			return;
		}

		// Handle main product image upload
		let mainImageUrl = '';
		if (imageFile) {
			mainImageUrl = await handleImageUpload(imageFile);
			values.imageSrc = mainImageUrl;
		} else if (initialImage === null) {
			// If initial image was deleted, send empty string to remove it
			values.imageSrc = '';
		} else if (initialImage) {
			// If initial image exists, keep it
			values.imageSrc = initialImage;
			mainImageUrl = initialImage;
		}

		// Handle variant image uploads
		const processedVariants = await Promise.all(
			variants.map(async (variant) => {
				const processedVariant = { ...variant };

				if (variant.imageFile) {
					if (variant.image === 'current-product-image') {
						// Use the main product image URL
						processedVariant.image = mainImageUrl;
					} else if (
						variant.image === 'new-upload' ||
						variant.image === 'variant-image'
					) {
						// Upload the new image or variant image
						const imageUrl = await handleImageUpload(variant.imageFile);
						processedVariant.image = imageUrl;
					}
					// Remove the imageFile property before sending to API
					delete processedVariant.imageFile;
				}

				return processedVariant;
			}),
		);

		values.variants = processedVariants;

		// Convert empty productInfo to null
		if (!values.productInfo || values.productInfo.trim() === '') {
			values.productInfo = null;
		}

		await handleSubmit(values);
	};

	return (
		<div className="flex flex-col w-full p-4">
			<h3 className="font-bold text-xl uppercase text-center mb-6">
				{isEditing ? 'Edit Product' : 'Add New Product'}
			</h3>
			<Formik
				initialValues={propInitialValues}
				onSubmit={handleFormSubmit}
				validationSchema={productSchema}
				validate={formikValidate}
				enableReinitialize={true}
			>
				<Form className="flex flex-wrap gap-4 justify-between w-full">
					{/* Basic Information */}
					<div className="w-full">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="space-y-1">
								{formFields.slice(0, 3).map((field) => (
									<FormField key={field.name} {...field} />
								))}
							</div>
							<div className="space-y-1">
								{formFields.slice(3).map((field) => (
									<FormField key={field.name} {...field} />
								))}

								{/* Image Upload Section */}
								<div className="mb-8">
									<h3 className="font-medium mb-2">Product Image</h3>
									<div className="flex items-center gap-3">
										{!imageFile && !initialImage && (
											<label className="flex items-center gap-4 cursor-pointer">
												<div className="flex items-center justify-center w-12 h-12 rounded-full border border-gray-300 text-gray-500">
													<FiUploadCloud size={24} />
												</div>
												<div>
													<p className="text-sm font-medium text-gray-700">
														Upload image
													</p>
													<p className="text-xs text-gray-400">
														{imageFile ? 'Image selected' : 'Select image'}
													</p>
												</div>
												<input
													type="file"
													accept="image/*"
													className="hidden"
													onChange={handleImageChange}
													aria-label="Upload product image"
												/>
											</label>
										)}
									</div>
									{/* Show existing image or new selected image */}
									{(imageFile || initialImage) && (
										<div className="mt-4 flex gap-4 flex-wrap">
											<div className="relative group">
												<img
													src={
														imageFile
															? URL.createObjectURL(imageFile)
															: initialImage || ''
													}
													alt={`Product preview`}
													className="w-32 h-32 object-contain rounded-lg border"
												/>
												<button
													type="button"
													onClick={() => handleImageDelete()}
													className="absolute top-1 right-1 bg-white bg-opacity-80 rounded-full p-1 text-black hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
													aria-label={`Delete image`}
													tabIndex={0}
												>
													<FiTrash2 />
												</button>
											</div>
										</div>
									)}
									{/* Custom Image validation error */}
									{customErrors.image && (
										<div className="text-red-500 text-xs mt-1">
											{customErrors.image}
										</div>
									)}
								</div>
							</div>
						</div>
					</div>

					<div className="border-t w-full my-4" />

					{/* Add Variant Button */}
					<div className="flex flex-row justify-between items-center w-full mb-2">
						<h4 className="font-semibold text-lg">Variants</h4>
						<Button
							type="button"
							size="small"
							shape="rounded"
							className="bg-purpleBrand-500 hover:bg-purpleBrand-600 text-white px-4 py-2 rounded focus:outline-none"
							aria-label="Add Variant"
							tabIndex={0}
							onClick={handleOpenVariantModal}
						>
							Add Variant
						</Button>
					</div>

					{/* Variants Table */}
					{variants.length > 0 ? (
						<div className="overflow-x-auto mb-6 rounded-lg border max-h-48 overflow-y-auto">
							<table className="w-full min-w-[845px] text-sm text-left">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-3 py-2 font-medium">Image</th>
										<th className="px-3 py-2 font-medium">Title</th>
										<th className="px-3 py-2 font-medium">Options</th>
										<th className="px-3 py-2 font-medium">Price</th>
										<th className="px-3 py-2 font-medium">Stock</th>
										<th className="px-3 py-2 font-medium">SKU</th>
										<th className="px-3 py-2 font-medium">Barcode</th>
										<th className="px-3 py-2 font-medium">Actions</th>
									</tr>
								</thead>
								<tbody>
									{variants.map((variant, idx) => {
										// Determine which image to show in the preview
										let imageToShow = '';
										if (
											(variant.image === 'current-product-image' ||
												variant.image === 'new-upload' ||
												variant.image === 'variant-image') &&
											variant.imageFile
										) {
											imageToShow = URL.createObjectURL(variant.imageFile);
										} else if (
											variant.image &&
											variant.image !== 'current-product-image' &&
											variant.image !== 'new-upload' &&
											variant.image !== 'variant-image'
										) {
											imageToShow = variant.image;
										}

										return (
											<tr key={idx} className="even:bg-gray-50">
												<td className="px-3 py-2">
													{imageToShow ? (
														<img
															src={imageToShow}
															alt="variant"
															className="w-10 h-10 object-contain rounded"
														/>
													) : (
														<span className="inline-block w-5 h-5 bg-gray-200 rounded" />
													)}
												</td>
												<td className="px-3 py-2">{variant.type}</td>
												<td className="px-3 py-2">{variant.name}</td>
												<td className="px-3 py-2">
													{formatPrice(variant.price)}
												</td>
												<td className="px-3 py-2">{variant.inventoryQty}</td>
												<td className="px-3 py-2">{variant.sku}</td>
												<td className="px-3 py-2">{variant.barcode}</td>
												<td className="px-3 py-2">
													<button
														type="button"
														onClick={() => handleEditVariant(idx)}
														className="text-blue-600 hover:text-blue-800 p-1"
														aria-label="Edit variant"
													>
														<FiEdit size={14} />
													</button>
													<button
														type="button"
														onClick={() => handleRemoveVariant(idx)}
														className="text-red-600 hover:text-red-800 p-1"
														aria-label="Remove variant"
													>
														<FiTrash2 size={14} />
													</button>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					) : (
						<p className="text-gray-500 mb-6">No variants added yet.</p>
					)}

					{/* Custom Variants validation error */}
					{customErrors.variants && (
						<div className="text-red-500 text-xs mb-4 w-full">
							{customErrors.variants}
						</div>
					)}

					<VariantModal
						isOpen={isVariantModalOpen}
						onClose={handleCloseVariantModal}
						onSubmit={handleAddVariant}
						onUpdate={handleUpdateVariant}
						uploadedImages={uploadedImages}
						currentProductImage={imageFile}
						currentProductImageUrl={initialImage}
						variantImageFiles={variantImageFiles}
						editingVariant={
							isEditingVariant && editingVariantIndex !== null
								? variants[editingVariantIndex]
								: undefined
						}
						editingIndex={editingVariantIndex ?? undefined}
						isEditing={isEditingVariant}
					/>

					<div className="flex flex-row gap-4 w-full">
						<Button
							type="submit"
							className="flex-1 bg-purpleBrand-500 hover:bg-purpleBrand-600"
							shape="rounded"
							data-test="submit-product-button"
							isLoading={loading}
							disabled={loading}
						>
							{isEditing ? 'Update Product' : 'Create Product'}
						</Button>
					</div>
				</Form>
			</Formik>
		</div>
	);
};

export default ProductForm;
